// A KEZDŐLAP RÁCSA — a csempe → rács-oszlop-szélesség leképezés TISZTA függvénye és a rács közös osztályai.
//
// EGY folytonos CSS-rács (nincs sáv, nincs hasáb-verem): a csempék a felhasználó sorrendjében állnak a DOM-ban, a
// `grid-auto-flow: row dense` (md-től) pedig a lyukakat a KÉSŐBBI, beleférő csempékkel tölti ki — vizuálisan csak
// ennyit mozdul a sorrend. Mobilon (<md) egy oszlop, pontosan a lista sorrendje, dense nélkül.
//
// A MENTETT ADATMODELL VÁLTOZATLAN (size 1|2, a KPI figyelmen kívül hagyja): a méret-NÉV csak megjelenítés —
//   kicsi (KPI, `kind: 'stat'`) · közepes (size 1) · széles (size 2).
//
// OSZLOP-SZÁMOK (a rács oszlopai): <md 1 · md 6 · lg+ 12.
//   kicsi:   md 3 (2/sor) · lg 6 (2/sor — 1024–1279 px-en a 4/soros KPI a sparkline-nal nem fér ki) · xl 3 (4/sor)
//   közepes: md 6 · lg 6 · xl 6 · 2xl 4 (3/sor — 2xl-en a 2 hasáb túl széles, a régi 3 hasábos kép marad)
//   széles:  md 6 · lg 12 · xl 12 · 2xl 12
import { type DashboardTileSize } from '@atlas/shared';

export type TileSpanKind = 'small' | 'medium' | 'wide';
export type GridBreakpoint = 'md' | 'lg' | 'xl' | '2xl';
/** A breakpoint-onkénti oszlop-szélesség (a `base` mobilon mindig 1: egy oszlop). */
export type TileSpans = Record<GridBreakpoint, number> & { base: 1 };

const SPANS: Record<TileSpanKind, TileSpans> = {
  small: { base: 1, md: 3, lg: 6, xl: 3, '2xl': 3 },
  medium: { base: 1, md: 6, lg: 6, xl: 6, '2xl': 4 },
  wide: { base: 1, md: 6, lg: 12, xl: 12, '2xl': 12 },
};

/** A csempe méret-neve: a KPI (stat) mindig kicsi, a panel a mentett méret szerint közepes/széles. */
export function tileSpanKind(tile: {
  def: { kind: string };
  size: DashboardTileSize;
}): TileSpanKind {
  if (tile.def.kind === 'stat') return 'small';
  return tile.size === 2 ? 'wide' : 'medium';
}

export function tileSpans(kind: TileSpanKind): TileSpans {
  return SPANS[kind];
}

// A Tailwind csak a TELJES, literális osztály-nevet látja → a számokhoz statikus keresőtábla tartozik.
const COL_SPAN: Record<GridBreakpoint, Record<number, string>> = {
  md: { 3: 'md:col-span-3', 6: 'md:col-span-6' },
  lg: { 6: 'lg:col-span-6', 12: 'lg:col-span-12' },
  xl: { 3: 'xl:col-span-3', 6: 'xl:col-span-6', 12: 'xl:col-span-12' },
  '2xl': { 3: '2xl:col-span-3', 4: '2xl:col-span-4', 6: '2xl:col-span-6', 12: '2xl:col-span-12' },
};
const BREAKPOINTS: readonly GridBreakpoint[] = ['md', 'lg', 'xl', '2xl'];

/** A csempe `col-span-*` osztályai; az előző breakpointtal egyező érték nem ismétlődik (örökli). */
export function tileSpanClass(kind: TileSpanKind): string {
  const spans = SPANS[kind];
  const classes: string[] = [];
  let prev: number = spans.base;
  for (const bp of BREAKPOINTS) {
    const n = spans[bp];
    if (n !== prev) {
      const cls = COL_SPAN[bp][n];
      if (cls) classes.push(cls);
    }
    prev = n;
  }
  return classes.join(' ');
}

/**
 * A rács közös osztályai (nézet ÉS szerkesztő): mobilon 1 oszlop (lista-sorrend, dense nélkül), md-től 6, lg-től 12
 * oszlop `row dense` kitöltéssel. `gap-4` mindenhol; a sor-magasság a legmagasabb csempéé (alap `align-items: stretch`).
 */
export const DASHBOARD_GRID_CLASS =
  'grid grid-cols-1 gap-4 md:grid-cols-6 md:grid-flow-row-dense lg:grid-cols-12';
